/**
 * api-fetch/workflow — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  Card,
  CardAiThread,
  CardAttachment,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  Notebook,
  NotebookActionItemT,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardSuggestActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  KanbanApi,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  PromoteLaneToBoardResult,
  RuleRequestApplyResult,
  RuleRequestProposeResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TagPatch,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  MeetingAnalyzeResult,
  MeetingLivePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import type { ApiFetchContext } from './context'
import { KanbanAllocationConflictError } from './context'
import { proposeRuleRequestViaStream } from './proposeRuleRequestStream'

export function createWorkflowApi(ctx: ApiFetchContext) {
  const { _request, _trelloRequest, _casHeaders, getAuthHeaders, baseUrl, buildUrl } = ctx
  return {
    // Card checklists -------------------------------------------------
    listChecklists: (cardId) =>
      _request<CardChecklistT[]>('GET', `/cards/${cardId}/checklists/`),
    createChecklist: (cardId, title) =>
      _request<CardChecklistT>('POST', `/cards/${cardId}/checklists/`, { title }),
    updateChecklist: (checklistId, patch) =>
      _request<CardChecklistT>('PATCH', `/checklists/${checklistId}/`, patch),
    deleteChecklist: async (checklistId) => {
      await _request<void>('DELETE', `/checklists/${checklistId}/`)
    },

    // FEAT-2026-06-17-KANBAN-FOLLOWUP — local-LLM "Structure with AI".
    // The endpoint always returns 200 with an `available` flag; we still
    // swallow transport errors (offline / 5xx) into `available:false` so
    // the drawer falls back to the free heuristic chips instead of
    // surfacing an error for a non-critical, on-demand convenience.
    parseCardActions: async (cardId, text) => {
      try {
        return await _request<CardParseActionsResult>(
          'POST', `/cards/${cardId}/parse-actions/`,
          text === undefined ? {} : { text },
        )
      } catch {
        return { available: false, is_action: false, actions: [] }
      }
    },

    // U23 — live tick/cross suggestions from the draft note. Same
    // fail-soft contract as parseCardActions: transport errors become
    // `available:false` so the strip hides instead of erroring.
    suggestCardActions: async (cardId, text) => {
      try {
        return await _request<CardSuggestActionsResult>(
          'POST', `/cards/${cardId}/suggest-actions/`, { text },
        )
      } catch {
        return { available: false, reason: 'transport', suggestions: [] }
      }
    },

    createChecklistItem: (input) =>
      _request<CardChecklistItemT>('POST', `/checklist-items/`, input),
    updateChecklistItem: (itemId, patch) =>
      _request<CardChecklistItemT>('PATCH', `/checklist-items/${itemId}/`, patch),
    deleteChecklistItem: async (itemId) => {
      await _request<void>('DELETE', `/checklist-items/${itemId}/`)
    },
    // Stack 12 MR-B — tree write endpoints. The server-side @action
    // names + url_paths are pinned at
    // quotech-backend/email_kanban/tests/test_stack12_mr_b_checklist_item_move_pin.py.
    moveChecklistItem: (itemId, body) =>
      _request<CardChecklistItemT>(
        'POST', `/checklist-items/${itemId}/move/`, body,
      ),
    detachChecklistItem: (itemId) =>
      _request<CardChecklistItemT>(
        'POST', `/checklist-items/${itemId}/detach/`, {},
      ),

    promoteChecklistItemToCard: (itemId) =>
      _request<Card>(
        'POST', `/checklist-items/${itemId}/promote-to-card/`, {},
      ),

    // Tags ------------------------------------------------------------
    listTags: (orgId: number) =>
      _request<Tag[]>('GET', '/tags/', undefined, { organisation: orgId }),
    createTag: (input: TagCreate) => _request<Tag>('POST', '/tags/', input),
    // ARC-4 U09 — label side-editor write path. Recolouring a Tag row
    // recolours every card wearing it (cards reference the Tag).
    updateTag: (tagId: string, patch: TagPatch) =>
      _request<Tag>('PATCH', `/tags/${tagId}/`, patch),

    // Allocations -----------------------------------------------------
    listAllocations: (filter: AllocationFilter) =>
      _request<Allocation[]>('GET', '/allocations/', undefined, {
        email: filter.emailId,
        card: filter.cardId,
        active_only: filter.activeOnly === false ? '0' : '1',
      }),
    createAllocation: (input: AllocationCreate) =>
      _request<Allocation>('POST', '/allocations/', input),
    revokeAllocation: async (id, reason) => {
      await _request<void>('DELETE', `/allocations/${id}/`, { reason })
    },

    // Module A — atomic + idempotent allocate via lane action.
    // We DON'T use `_request` here because we need to inspect a
    // 409 response and surface its body as a typed error rather
    // than letting `_request` throw a generic HTTP error.
    allocateEmailToLane: async (input: AllocateEmailToLaneInput) => {
      const url = buildUrl(baseUrl, `/lanes/${input.laneId}/allocate-email/`)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const body = JSON.stringify({
        email_id: input.emailId,
        source: input.source,
        title_override: input.titleOverride,
        confidence: input.confidence,
        classification_snapshot: input.classificationSnapshot,
        // EK-14 — pass through when set; BE silently ignores stale
        // / cross-org / cross-email values so the FE doesn't need
        // to guard.
        supersedes_allocation_id: input.supersedesAllocationId ?? undefined,
        supersede_reason: input.supersedeReason,
        // Multi-placement opt-in — only sent when true so older BEs
        // (and every existing caller) keep the 409 conflict contract.
        allow_additional: input.allowAdditional || undefined,
      })
      const resp = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body,
      })
      if (resp.status === 409) {
        // Server says the email is on a card in a different lane.
        // Parse the conflict payload and throw a structured error.
        let conflict: KanbanAllocationConflict | undefined
        try {
          const data = await resp.json()
          conflict = (data && data.conflict) as KanbanAllocationConflict
        } catch {
          // body wasn't JSON for some reason — fall through to
          // generic error below
        }
        if (conflict) throw new KanbanAllocationConflictError(conflict)
      }
      if (!resp.ok) {
        const text = await resp.text().catch(() => '')
        throw new Error(`Allocate failed (${resp.status}): ${text || resp.statusText}`)
      }
      const data = (await resp.json()) as AllocateEmailToLaneResult
      return data
    },

    // FEAT-2026-06-24-EMAIL-LINK-EXISTING-CARD — link an email to an
    // existing card (sibling of allocateEmailToLane, which creates one).
    linkEmailToCard: async (input: LinkEmailToCardInput) =>
      _request<LinkEmailToCardResult>(
        'POST',
        `/cards/${input.cardId}/link-email/`,
        {
          email_id: input.emailId,
          source: input.source,
        },
      ),

    // Arc B — detach an email from a card (revokes allocation +
    // visibility, prunes related_emails, re-points primary).
    unlinkEmailFromCard: async (input: { cardId: string; emailId: string }) =>
      _request<{ card: Card }>(
        'POST',
        `/cards/${input.cardId}/unlink-email/`,
        { email_id: input.emailId },
      ),

    // Arc B — promote an already-linked email to primary (swap).
    setPrimaryEmail: async (input: { cardId: string; emailId: string }) =>
      _request<{ card: Card }>(
        'POST',
        `/cards/${input.cardId}/set-primary-email/`,
        { email_id: input.emailId },
      ),

    // FEAT-2026-06-24-EMAIL-LINK-EXISTING-CARD — typeahead card search
    // for the "link to existing card" picker.
    searchCards: (
      query: string,
      opts?: { boardId?: string; organisationId?: number },
    ) =>
      _request<CardSearchResult[]>('GET', '/cards/search/', undefined, {
        q: query,
        board: opts?.boardId,
        organisation: opts?.organisationId,
      }),

    confirmAiAllocation: (id, reason) =>
      _request<Allocation>('POST', `/allocations/${id}/confirm/`, { reason }),

    acceptAllocation: (id, reason) =>
      _request<Allocation>('POST', `/allocations/${id}/accept/`, { reason }),

    rejectAiAllocation: async (id, payload) => {
      await _request<void>('POST', `/allocations/${id}/reject/`, {
        reason: payload?.reason,
        correct_lane_id: payload?.correctLaneId ?? undefined,
      })
    },

    // EK-14 — "Why this board?" explainer (read-only).
    explainAllocation: (id) =>
      _request<AllocationExplain>('GET', `/allocations/${id}/explain/`),

    // Action templates -----------------------------------------------
    listActionTemplates: (filter?: ActionTemplateFilter) =>
      _request<ActionTemplate[]>('GET', '/action-templates/', undefined, {
        organisation: filter?.organisationId,
        lane_kind: filter?.laneKind,
      }),

    // Classification --------------------------------------------------
    listClassificationRules: (orgId: number) =>
      _request<ClassificationRule[]>(
        'GET',
        '/classification-rules/',
        undefined,
        { organisation: orgId },
      ),
    getMemory: (orgId: number) =>
      _request<OrgClassificationMemory>('GET', '/memory/', undefined, {
        organisation: orgId,
      }).then((r) => (Array.isArray(r) ? (r[0] as OrgClassificationMemory) : r)),
    rebuildMemory: (memoryId: string) =>
      _request<OrgClassificationMemory>('POST', `/memory/${memoryId}/rebuild/`),
    // FEAT-2026-06-14-KANBAN-SYSTEM-MAP Phase 2b — editable governance.
    // Standard DRF partial_update; only the patched fields are sent.
    updateClassificationRule: (id, patch) =>
      _request<ClassificationRule>('PATCH', `/classification-rules/${id}/`, patch),
    // FEAT-2026-06-14-KANBAN-SYSTEM-MAP Phase 2d — author a new rule.
    // Standard DRF create; created_by is stamped server-side.
    createClassificationRule: (input) =>
      _request<ClassificationRule>('POST', '/classification-rules/', input),

    // FEAT-2026-07-06-REQUEST-A-RULE — NL → dry-run proposal (no writes).
    // 422 (unparseable / failed validation) and 502 (LLM down) surface as
    // KanbanNetworkError with the server's operator-facing `error` string.
    proposeRuleRequest: ({ organisationId, instruction, emailId }) =>
      _request<RuleRequestProposeResult>('POST', '/rule-requests/propose/', {
        organisation_id: organisationId,
        instruction,
        ...(emailId ? { email_id: emailId } : {}),
      }),
    // Phase C C-01 — same result shape via SSE propose-stream.
    proposeRuleRequestStream: (input) =>
      proposeRuleRequestViaStream({ baseUrl, getAuthHeaders }, input),
    // FEAT-2026-07-06-REQUEST-A-RULE — operator-approved apply. Atomic
    // server-side: board + sub-board + lane + rule land together or not
    // at all (re-validated, so a stale proposal 400s instead of half-creating).
    // BUG-2026-08-10-REQUEST-A-RULE-APPLY-PREVIEW-ID: live BE expects the
    // proposal object (views/rule_requests.py), not a Phase-C preview_id.
    applyRuleRequest: ({ organisationId, proposal }) =>
      _request<RuleRequestApplyResult>('POST', '/rule-requests/apply/', {
        organisation_id: organisationId,
        proposal,
      }),

    // FEAT-2026-06-14-KANBAN-SYSTEM-MAP — live quote-stage rollup ($ value
    // + volume per stage) for the System Map. Unwraps the `{stages: [...]}`
    // envelope so callers get the row array directly (mirrors getMemory).
    getQuoteStageSummary: (organisationId?: number) =>
      _request<{ stages: QuoteStageSummaryRow[] }>(
        'GET',
        '/boards/quote-stage-summary/',
        undefined,
        { organisation: organisationId },
      ).then((r) => r?.stages ?? []),

    // Search ----------------------------------------------------------
    //
    // Bypasses `_request` because search uniquely needs:
    //   1. AbortSignal forwarding (the palette aborts in-flight
    //      requests on every keystroke)
    //   2. No-throw on AbortError (the consumer treats abort as a
    //      normal "newer query already in flight" signal)
    //   3. No 412-conflict handling (search is a pure GET)
    //
    // Returns the raw response shape from
    // `GET /api/kanban/search/`. The discriminated `type` field
    // on each result row drives FE rendering.
    searchKanban: async (opts) => {
      const types = opts.types && opts.types.length
        ? opts.types.join(',')
        : undefined
      const url = buildUrl(baseUrl, '/search/', {
        q: opts.q,
        types,
        limit: opts.limit,
        include_archived: opts.includeArchived ? 1 : undefined,
        // Board-first global search (2026-06-03). Both optional; the
        // BE ignores unknown/blank values so older backends just
        // return the global result set.
        board_id: opts.boardId,
        exclude_board_id: opts.excludeBoardId,
        // G6 — scope every result type to a single board (full-body
        // card matches the lean board projection hides client-side).
        only_board_id: opts.onlyBoardId,
        organisation: opts.organisationId,
      })
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const res = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: opts.signal,
      })
      if (!res.ok) {
        // 400 carries our structured "min query length" body.
        // Surface it cleanly so the consumer can show a hint
        // rather than blow up.
        if (res.status === 400) {
          try {
            const body = await res.json()
            return body as KanbanSearchResponse
          } catch {
            /* fall through */
          }
        }
        throw new Error(`searchKanban failed: ${res.status} ${res.statusText}`)
      }
      return (await res.json()) as KanbanSearchResponse
    },

    // satisfies Partial<KanbanApi> — see api-fetch/cards.ts for the rationale
    // (back-propagates KanbanApi param types under tenant strict mode).
  } satisfies Partial<KanbanApi>
}
